<template>
  <div>
    <v-dialog v-model="dialog" eager persistent scrollable max-width="560">
      <v-form ref="FeedbackForm" @submit.prevent>
        <v-card class="feedback-card" elevation="0">
          <!-- Header -->
          <div class="dialog-header">
            <div class="header-left">
              <div class="header-icon-wrap">
                <v-icon size="18" color="white"
                  >mdi-message-text-outline</v-icon
                >
              </div>
              <div>
                <div class="header-title text-white">{{ action }} Feedback</div>
                <div class="header-sub text-white">
                  Share your thoughts about an intern
                </div>
              </div>
            </div>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              class="close-btn"
              @click="closeD()"
            />
          </div>

          <v-divider class="header-divider" />

          <v-card-text class="dialog-body">
            <!-- Step 1: Feedback type -->
            <div class="step-block">
              <div class="step-label">
                <span class="step-num">1</span>
                Feedback type
              </div>
              <div class="type-grid">
                <div
                  class="type-card"
                  :class="{ active: feedback_type === 'general' }"
                  @click="action == 'View' ? null : (feedback_type = 'general')"
                >
                  <div
                    class="type-icon"
                    :class="{ active: feedback_type === 'general' }"
                  >
                    <v-icon size="20">mdi-lightbulb-outline</v-icon>
                  </div>
                  <div class="type-info">
                    <div class="type-name">Comment</div>
                    <div class="type-desc">General observation or note</div>
                  </div>
                  <div class="type-check" v-if="feedback_type === 'general'">
                    <v-icon size="16" color="white">mdi-check</v-icon>
                  </div>
                </div>

                <div
                  class="type-card"
                  :class="{ active: feedback_type === 'concern' }"
                  @click="action == 'View' ? null : (feedback_type = 'concern')"
                >
                  <div
                    class="type-icon"
                    :class="{ active: feedback_type === 'concern' }"
                  >
                    <v-icon size="20">mdi-account-search-outline</v-icon>
                  </div>
                  <div class="type-info">
                    <div class="type-name">Concerns</div>
                    <div class="type-desc">Issue that needs attention</div>
                  </div>
                  <div class="type-check" v-if="feedback_type === 'concern'">
                    <v-icon size="16" color="white">mdi-check</v-icon>
                  </div>
                </div>
              </div>
              <div v-if="showTypeError" class="field-error">
                <v-icon size="14">mdi-alert-circle-outline</v-icon>
                Please select a feedback type
              </div>
            </div>

            <!-- Step 2 & 3 shown after type selected -->
            <transition name="fade-slide">
              <div v-if="feedback_type">
                <!-- Step 2: Select Intern -->
                <div class="step-block">
                  <div class="step-label">
                    <span class="step-num">2</span>
                    Select intern(s)
                  </div>
                  <v-autocomplete
                    density="compact"
                    placeholder="Search and select interns..."
                    v-model="intern_to_manage"
                    :color="$vuetify.theme.themes.light.inputColor"
                    variant="outlined"
                    :rules="[
                      (v) =>
                        (v && v.length > 0) ||
                        'Please select at least one intern',
                    ]"
                    :readonly="action == 'View'"
                    multiple
                    item-value="id"
                    item-title="name"
                    closable-chips
                    chips
                    :items="internlist"
                    class="custom-field"
                    hide-details="auto"
                    prepend-inner-icon="mdi-account-search-outline"
                  >
                    <template #chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        color="#16a34a"
                        variant="tonal"
                        size="small"
                        class="intern-chip"
                      >
                        <template #prepend>
                          <div class="chip-avatar">
                            {{ item.title?.charAt(0) }}
                          </div>
                        </template>
                        {{ item.title }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </div>

                <!-- Step 3: Feedback text -->
                <div class="step-block last">
                  <div class="step-label">
                    <span class="step-num">3</span>
                    Write your feedback
                  </div>
                  <v-textarea
                    placeholder="Describe your feedback in detail..."
                    :rules="[formRules.required]"
                    :readonly="action == 'View'"
                    v-model="feedback"
                    variant="outlined"
                    density="compact"
                    rows="4"
                    no-resize
                    :color="$vuetify.theme.themes.light.inputColor"
                    class="custom-field"
                    hide-details="auto"
                    counter="500"
                    maxlength="500"
                  />
                </div>
              </div>
            </transition>
          </v-card-text>

          <v-divider />

          <!-- Actions -->
          <v-card-actions class="dialog-actions" v-if="action != 'View'">
            <v-btn class="cancel-btn" variant="text" @click="closeD()">
              <v-icon size="16" class="mr-1">mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              class="submit-btn"
              variant="flat"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="action == 'Add' ? save() : update()"
            >
              <v-icon size="16" class="mr-1">mdi-check-circle-outline</v-icon>
              {{ action == "Add" ? "Submit Feedback" : "Update Feedback" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import eventBus from "@/eventBus";
export default {
  props: {
    data: Object,
    action: String,
  },
  data() {
    return {
      dialog: false,
      feedback_type: null,
      feedback: null,
      btnLoading: false,
      intern_to_manage: [],
      internlist: [],
      showTypeError: false,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
    };
  },
  watch: {
    data: {
      handler(data) {
        console.log(data);
        this.dialog = true;
        this.showTypeError = false;
        this.loadInterns();
        if (data.id) {
          this.feedback_type = data.feedback_type;
          this.feedback = data.feedback;
          for (let i = 0; i < data.interns.length; i++) {
            this.intern_to_manage.push(data.interns[i].student_id.toString());
          }
        } else {
          this.feedback_type = null;
          this.feedback = null;
          this.intern_to_manage = [];
        }
        this.$nextTick(() => {
          this.$refs.FeedbackForm?.resetValidation();
        });
      },
      deep: true,
    },
    feedback_type(val) {
      if (val) this.showTypeError = false;
    },
  },

  computed: {
    getStudentHandled() {
      let handled = [];
      if (this.data) {
        if (this.data.handled_interns[0].length > 0) {
          for (let i = 0; i < this.data.handled_interns[0].length; i++) {
            handled.push(this.data.handled_interns[0][i].student.id);
          }
        }
      }
      return handled;
    },
    getIsFeedbackByFieldOfficer() {
      return this.$store.state.user.user.usertypeID == 9;
    },
  },
  methods: {
    loadInterns() {
      this.axiosCall("/organization/getMyInterns", "GET").then((res) => {
        this.internlist = res.data;
      });
    },

    async save() {
      if (!this.feedback_type) {
        this.showTypeError = true;
        return;
      }

      this.btnLoading = true;
      await this.$nextTick();
      const validate = await this.$refs.FeedbackForm.validate();

      if (validate.valid) {
        let data = {
          feedback: this.feedback,
          feedback_type: this.feedback_type,
          is_feedback_by_field_officer: this.getIsFeedbackByFieldOfficer,
          interns: this.intern_to_manage,
        };
        this.axiosCall("/organization-intern-feedback", "POST", data).then(
          (res) => {
            if (res.data.status == 201) {
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "success";
              this.fadeAwayMessage.header = "System Message";
              this.fadeAwayMessage.message = res.data.msg;
              this.closeD();
            } else if (res.data.status == 400) {
              this.btnLoading = false;
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "error";
              this.fadeAwayMessage.header = "System Message";
              this.fadeAwayMessage.message = res.data.msg;
            }
          },
        );
      } else {
        this.btnLoading = false;
      }
    },

    async update() {
      this.btnLoading = true;
      const validate = await this.$refs.FeedbackForm.validate();
      if (validate.valid) {
        let data = {
          feedback: this.feedback,
          feedback_type: this.feedback_type,
          is_feedback_by_field_officer: this.getIsFeedbackByFieldOfficer,
          interns: this.intern_to_manage,
        };
        this.axiosCall(
          "/organization-intern-feedback/" + this.data.id,
          "PATCH",
          data,
        ).then((res) => {
          if (res.data.status == 200) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
            this.closeD();
          } else if (res.data.status == 400) {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        });
      } else {
        this.btnLoading = false;
      }
    },

    closeD() {
      eventBus.emit("closeFeedbackDialog", true);
      this.btnLoading = false;
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
/* ── Card ── */
.feedback-card {
  border-radius: 16px !important;
  overflow: hidden;
  font-family: "DM Sans", sans-serif;
}

/* ── Header ── */
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  background: #fff;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #16a34a, #22c55e);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.35);
}
.header-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.2px;
}
.header-sub {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 1px;
}
.header-divider {
  opacity: 0.06;
}
.close-btn {
  color: #9ca3af !important;
}
.close-btn:hover {
  color: #374151 !important;
  background: #f3f4f6 !important;
}

/* ── Body ── */
.dialog-body {
  padding: 20px 24px !important;
  background: #f9fafb;
}

/* ── Steps ── */
.step-block {
  margin-bottom: 20px;
}
.step-block.last {
  margin-bottom: 0;
}
.step-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}
.step-num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #16a34a;
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Type cards ── */
.type-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.type-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 14px;
  border-radius: 12px;
  border: 1.5px solid #e5e7eb;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.type-card:hover {
  border-color: #86efac;
  background: #f0fdf4;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.1);
}
.type-card.active {
  border-color: #16a34a;
  background: #f0fdf4;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
}
.type-icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.18s ease;
  flex-shrink: 0;
}
.type-icon.active {
  background: #16a34a;
  color: #fff;
  box-shadow: 0 4px 10px rgba(22, 163, 74, 0.3);
}
.type-info {
  flex: 1;
}
.type-name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}
.type-desc {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 1px;
}
.type-check {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Field error ── */
.field-error {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
  padding-left: 2px;
}

/* ── Custom fields ── */
.custom-field :deep(.v-field) {
  border-radius: 10px !important;
  background: #ffffff !important;
  font-size: 13px;
}
.custom-field :deep(.v-field--focused) {
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.15) !important;
}

/* ── Intern chip ── */
.intern-chip {
  font-size: 12px !important;
  font-weight: 500 !important;
}
.chip-avatar {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #16a34a;
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
}

/* ── Actions ── */
.dialog-actions {
  padding: 14px 20px !important;
  background: #fff;
  gap: 8px;
  justify-content: flex-end;
}
.cancel-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #6b7280 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
  padding: 0 14px !important;
}
.cancel-btn:hover {
  background: #f3f4f6 !important;
  color: #374151 !important;
}
.submit-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg, #16a34a, #22c55e) !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
  padding: 0 18px !important;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3) !important;
  transition: all 0.18s ease !important;
}
.submit-btn:hover {
  box-shadow: 0 6px 16px rgba(22, 163, 74, 0.4) !important;
  transform: translateY(-1px);
}
.submit-btn:disabled {
  opacity: 0.6 !important;
}

/* ── Transition ── */
.fade-slide-enter-active {
  transition: all 0.25s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
